<template>
  <div class="productRecommendations">
    <div class="productRecommendations__header">
      <div class="productRecommendations__title">
        <PhSparkle :size="16" />
        {{ $t('product.recommendations.title') }}
      </div>
      <div v-if="subtitle" class="productRecommendations__subtitle">{{ subtitle }}</div>
    </div>
    <div v-if="isLoading" class="productRecommendations__loading">
      <sl-spinner></sl-spinner>
    </div>
    <div v-else-if="recommendations.length" class="productRecommendations__grid">
      <div v-for="item in recommendations" :key="item.sku" class="productRecommendations__item">
        <ProductCard :item="item" />
        <sl-button variant="default" size="small" @click="goToProduct(item)">
          {{ $t('product.recommendations.load') }}
        </sl-button>
      </div>
    </div>
    <div v-else class="productRecommendations__empty">
      {{ $t('product.recommendations.empty') }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { PhSparkle } from '@phosphor-icons/vue'
import '@shoelace-style/shoelace/dist/components/button/button'
import '@shoelace-style/shoelace/dist/components/spinner/spinner'

import ProductCard from '@/components/Product/ProductCard.vue'
import { useNbp } from '@/composables/useNbp.js'

const props = defineProps({
  customerEmail: {
    type: String,
    default: null
  },
  active: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

const { t } = useI18n()
const router = useRouter()
const { getProductRecommendations } = useNbp()

const recommendations = ref([])
const divergence = ref(null)
const useCase = ref(null)
const isLoading = ref(false)

// The subtitle names the use case it summarises ('optical orders' / 'sunglasses orders'), so
// each one gets its own string rather than an interpolated noun — the grammar differs per
// language, and German compounds the sun variant.
const subtitle = computed(() => {
  if (!divergence.value || !useCase.value) return null

  return t(`product.recommendations.subtitle_${useCase.value}`, {
    count: divergence.value.keep_count,
    shape: divergence.value.modal_shape,
    shapeScore: divergence.value.shape_score,
    size: divergence.value.modal_size,
    sizeScore: divergence.value.size_score,
    gender: divergence.value.modal_gender,
    genderScore: divergence.value.gender_score
  })
})

function goToProduct(item) {
  emit('close')
  router.push({ name: 'product', params: { modelId: item.modelId, sku: item.sku } })
}

watch(
  () => props.active,
  async (active) => {
    if (!active) return

    recommendations.value = []
    divergence.value = null
    useCase.value = null
    // Set before awaiting so the empty message never shows while the lookup is still running.
    isLoading.value = true

    try {
      const result = await getProductRecommendations(props.customerEmail)
      recommendations.value = result.recommendations
      divergence.value = result.divergence
      useCase.value = result.useCase
    } finally {
      isLoading.value = false
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.productRecommendations {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--sl-font-size-large);
    font-weight: var(--font--bold);
  }

  &__subtitle {
    font-size: var(--sl-font-size-small);
    color: var(--sl-color-neutral-500);
    line-height: 1.5;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 6px;

    sl-button {
      width: 100%;
    }
  }

  &__empty {
    font-size: var(--sl-font-size-small);
    color: var(--sl-color-neutral-500);
  }

  &__loading {
    display: flex;
    justify-content: center;
    padding: 12px 0;
  }
}
</style>
